<template>
    <div class="sl-location-item"
         :class="{
             'open': location.open,
             'closed': !location.open
         }"

    >
        <button class="sl-location-toggle-btn" :class="{'featured': this.featured}" type="button" @click="toggleOpen">
            <div class="sl-location-header">
                <div>{{ location.displayName }}</div>
                <div v-if="!featured" class="sl-toggle-btns">
                    <svg v-if="location.open" width="24px" height="24px" stroke-width="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M6 12H18" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                    <svg v-else width="24px" height="24px" stroke-width="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M6 12H12M18 12H12M12 12V6M12 12V18" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
                </div>
            </div>
            <div v-if="canCalculateDistance" class="sl-distance">
                {{ location.distanceAway }} {{ state.distancePref }} away
            </div>
            <div class="sl-fitting-types">
                <div v-if="locationLabel" class="sl-selectfit-level">
                    <span class="sl-label-wrapper"><img :src="locationLabel.img" alt=""> {{ locationLabel.label }}</span>
                </div>
                <div v-if="isNFC" class="sl-selectfit-level">
                    <span class="sl-label-wrapper"><img :src="getIcon('NFC')" alt=""> National Fitters Council</span>
                </div>
            </div>
        </button>

        <a v-if="location.bookingLink"
           class="sl-book-ext-fitting"
           :href="location.bookingLink"
           target="_blank">
            Book a Fitting
        </a>

        <div v-if="location.events && location.events.length" class="sl-mfe-events">
            <a v-for="event in location.events"
               :key="event._uid"
               class="sl-mfe-event"
               :href="event.shareLink"
            >
                <div class="sl-mfe-event-dates">
                    <div><formatted-date :date="event.scheduledStart" /></div>
                    <formatted-date :time="event.scheduledStart" /> - <formatted-date :time="event.scheduledEnd" />
                </div>

                <span class="tw-uppercase tw-ml-auto tw-font-bold">Book an Appointment</span>
            </a>
        </div>

        <MfeBook v-if="mfeEventData" :fittings="mfeEventData" />

        <div v-if="location.open" class="sl-address">
            <svg class="sl-map-icon" width="24px" height="24px" stroke-width="2" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" color="#000000"><path d="M20 10C20 14.4183 12 22 12 22C12 22 4 14.4183 4 10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10Z" stroke="#000000" stroke-width="2" /><path d="M12 11C12.5523 11 13 10.5523 13 10C13 9.44772 12.5523 9 12 9C11.4477 9 11 9.44772 11 10C11 10.5523 11.4477 11 12 11Z" fill="#000000" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <div class="sl-street">
                {{ location.street }}, 
                {{ location.city }},
                {{ location.state }}, 
                {{ location.postalCode }}
            </div>
            <div class="sl-directions">
                <a target="_blank" :href="'https://www.google.com/maps/dir//' + location.street +','+ location.city +','+ location.state +','+ location.postalCode">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
                        <path d="M12.1113 4.55566L15.2224 7.66678L12.1113 10.7779" stroke="black" stroke-width="2.01333" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M15.2229 7.66602H9.88954C7.92586 7.66602 6.33398 9.25793 6.33398 11.2216V17.4438" stroke="black" stroke-width="2.01333" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </a>
            </div>
        </div>

        <section v-if="location.open" class="sl-offerings">
            <div class="sl-col-half">
                <div class="sl-col-heading">
                    Fitting Types
                </div>
                <ul class="sl-offerings-list">
                    <template v-if="!location.fittingTypes">
                        <li v-for="fitType in fittingTypes" :key="fitType._uid">
                            {{ fitType.label }}
                        </li>
                    </template>
                    <template v-if="location.fittingTypes">
                        <li v-for="fitType in location.fittingTypes" :key="fitType._uid">
                            {{ convertTitleCase(fitType.displayName).replace('Only', '') }}
                        </li>
                    </template>
                </ul>
            </div>
            <div class="sl-col-half">
                <div class="sl-col-heading">
                    Fitting Technology
                </div>
                <ul class="sl-offerings-list">
                    <li v-for="service in fittingServices" :key="service._uid">
                        {{ service.label }}
                    </li>
                    <li v-if="location.events.length">
                        MyFittingExp
                    </li>
                </ul>
            </div>
        </section> 
    </div>
</template>

<script>
import AzureMaps from '../utils/azureMaps';
import FormattedDate from './FormattedDate';
import sampleMfe from '../utils/sampleMfe';
import MfeBook from './MfeBook.vue';

const FITTING_SERVICES = {
    CORE: [{label: 'SelectFit'}],
    CERT: [{label: 'SelectFit'}],
    PREM: [{label: 'SelectFit'}]
};

const FITTING_TYPES = {
    CORE: [{label: 'Driver'}, {label: 'Irons'}],
    CERT: [{label: 'Driver'}, {label: 'Irons'}],
    PREM: [{label: 'Driver'}, {label: 'Fairway'}, {label: 'Rescue'}, {label: 'Irons'}, {label: 'Wedge'}, {label: 'Putter'}],
};

const LABEL_ICONS = {
    CORE    : 'storelocator_images/label_core.png',
    CERT    : 'storelocator_images/label_cert.png',
    PREM    : 'storelocator_images/label_prem.png',
    MFE     : 'storelocator_images/label_mfe.png',
    RETAILER: 'storelocator_images/label_retailer.png',
    NFC     : 'storelocator_images/label_nfc.png',
};

export default {
    components: {
        FormattedDate,
        MfeBook
    },

    props: ['location', 'featured'],

    data: function () {
        return {
            mfeEventData: null,
        };
    },

    computed: {
        canCalculateDistance () {
            if (window._AZURE_MAP.geoCodedSearchData) {
                return true;
            }

            return false;
        },
        isNFC () {
            return this.location.selectFitLogoType && this.location.selectFitLogoType.name == 'NFC';
        },
        
        locationLabel () {
            if (this.location.events && this.location.events.length) {
                return {img: this.getIcon('MFE'), label: 'TaylorMade Fitting Event'};
            }

            if (this.location.iconType == 'CORE') {
                return {img: this.getIcon('CORE'), label: 'Core Services'};
            }

            if (this.location.iconType == 'CERT') {
                return {img: this.getIcon('CERT'), label: 'Certified Services'};
            }

            if (this.location.iconType == 'PREM') {
                return {img: this.getIcon('PREM'), label: 'Premier Services'};
            }

            return false;
        },

        fittingTypes () {
            return FITTING_TYPES[this.location.selectFitEbsCode ? this.location.selectFitEbsCode.name : 'CORE'];
        },

        fittingServices () {
            return FITTING_SERVICES[this.location.selectFitEbsCode ? this.location.selectFitEbsCode.name : 'CORE'];
        },
    },

    mounted () {
        if (this.featured) {
            this.setOpen();
        }
    },

    methods: {
        convertTitleCase (str) {
            return str.replace(
                /\w\S*/g,
                text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
            );
        },
        setOpen () {
            const results = this.state.locations.filter(location => {
                return location.id == this.location.id;
            });

            results[0].open = true;
        },

        setClose () {
            const results = this.state.locations.filter(location => {
                return location.id == this.location.id;
            });

            results[0].open = false;
        },

        getIcon (iconType) {
            return this.state.imagePath.replace('__FILE__', LABEL_ICONS[iconType]);
        },
        toggleOpen () {
            if (this.featured) {
                return;
            }

            if (!this.location.open) {
                setTimeout(() => {
                    AzureMaps.setCameraPositionByLongLat(this.location.longitude, this.location.latitude);
                }, 250);
            }

            if (this.location.open) {
                this.setClose();
            } else {
                this.setOpen();
            }

            return false;
        },
        openIfClosed () {
            if (!this.location.open) {
                this.setOpen();
            }

            return true;
        },

        bookMfeAppointment () {
            this.mfeEventData = JSON.parse(sampleMfe.data);
        }
    }
};
</script>

<style scoped>
.sl-fix-case {
    text-transform: capitalize;
}
.sl-location-item {
    border-bottom: solid 1px #ddd;
    padding: 5px 10px 15px;
    background: #fff;
}

.sl-location-toggle-btn {
    appearance: none;
    background: none;
    border: none;
    width: 100%;
    padding: 10px 0 0;
    color: #000;
}

.sl-location-toggle-btn.featured {
    cursor: default;
}

.sl-location-header {
    font-weight: 700;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
}
.sl-toggle-btns {
    margin-left: auto;
}
.sl-toggle-btns svg {
    width: 25px;
}

.sl-distance {
    font-size: 12px;
    text-align: left;
    width: 100%;
}
.sl-fitting-types {
    display: flex;
    align-items: flex-start;
}
.sl-selectfit-level + .sl-selectfit-level {
    margin-left: 10px;
}
.sl-selectfit-level {
    margin-top: 10px;
    font-weight: bold;
    font-size: 12px;
    text-align: left;
}
.sl-label-wrapper {
    padding: 0 16px 0 0;
    background: #e2e2e2;
    border-radius: 16px;
    height: 34px;    
    display: inline-flex;
    align-items: center;
    height: 32px;
    font-size: 11px;
    letter-spacing: 0.24px;
}
.sl-label-wrapper img {
    display: inline-block;
    width: 30px;
    margin-right: 5px;
}
.sl-booking-alert {
    font-size: 12px;
}
.sl-book-ext-fitting {
    margin-top: 5px;
    background: #000;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 16px;
    height: 40px;
    padding: 5px 16px 5px 10px;
    font-weight: bold;
    font-size: 13px;  
    text-transform: uppercase;
    text-decoration: none;
    letter-spacing: 0.24px;    
}
.sl-book-ext-fitting:hover {
    background: #444;
}
.sl-book-ext-fitting svg {
    display: inline-block;
    width: 15px;
    margin-left: 5px;
}
.sl-mfe-events {
    padding-top: 5px;
}
.sl-mfe-event {
    appearance: none;
    border: none;
    width: 100%;
    background: rgba(0, 0, 0, 0.10);
    display: flex;
    text-align: left;
    align-items: center;
    border-radius: 30px;
    padding: 5px 16px 5px 10px;
    font-size: 13px;
    margin-bottom: 10px;
    text-decoration: none;
    border: solid 1px transparent;
    line-height: 1.3;    
    letter-spacing: 0.24px;
}
.sl-mfe-event:hover {
    background: rgba(0, 0, 0, 0.05);
}
.sl-mfe-event-dates {
    padding-left: 5px;

}
.sl-mfe-event-dates div {
    font-weight: 700;
}
.sl-offerings {
    display: flex;
    font-size: 12px;
}
.sl-col-half {
    width: 50%;
}
.sl-col-heading {
    min-height: 25px;
    font-weight: 700;
}
.sl-offerings-list {
    padding-left: 0;
    margin-left: 10px;
    list-style: disc;
}
.sl-offerings-list li {
    line-height: 110%;
    margin-bottom: 4px;
}
.sl-address {
    display: flex;
    align-items: center;
    margin: 10px 0;
}
.sl-map-icon {
    height: 15px;
    margin-right: 5px;
}
.sl-street {
    font-size: 11px;
    margin-right: auto;
}
.sl-directions a {
    margin-left: 5px;
    display:  flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.10);
    border-radius: 50%;
    height: 33px;
    width: 33px;
}
.sl-directions a:hover {
    background: rgba(0, 0, 0, 0.05);
}
</style>